// admin-panel/src/pages/BlogForm.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { blogApi } from '../lib/api';
import {
  ArrowLeft, Save, Upload, X, Eye, Star, Search,
  Globe, Share2, Tag, ImageIcon, Calendar, BookOpen, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = {
  title: '', slug: '', excerpt: '', content: '',
  blog_category_id: '', status: 'draft', is_featured: false,
  published_at: '', tags: [],
  // SEO
  meta_title: '', meta_description: '', meta_keywords: '',
  canonical_url: '', og_title: '', og_description: '', og_image: '',
};

const TABS = [
  { id: 'content', label: 'Content',  icon: BookOpen },
  { id: 'seo',     label: 'SEO',      icon: Globe },
  { id: 'og',      label: 'Open Graph', icon: Share2 },
  { id: 'publish', label: 'Publish',  icon: Calendar },
];

/* Simple slug generator */
function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

/* Character counter badge */
function CharCount({ value = '', max, warn = 0.85 }) {
  const pct = value.length / max;
  const cls = pct > 1 ? 'text-red-400' : pct > warn ? 'text-yellow-400' : 'text-[#555]';
  return <span className={`text-[11px] ${cls}`}>{value.length}/{max}</span>;
}

export default function BlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;
  const imageRef = useRef();
  const ogImageRef = useRef();

  const [form, setForm] = useState(EMPTY);
  const [activeTab, setActiveTab] = useState('content');
  const [tagInput, setTagInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);
  const [ogImagePreview, setOgImagePreview] = useState(null);
  const [autoSlug, setAutoSlug] = useState(!isEdit);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ── Fetch categories ── */
  const { data: catsData = [] } = useQuery({
    queryKey: ['blog-categories-public'],
    queryFn: blogApi.listCategories,
  });
  const categories = Array.isArray(catsData) ? catsData : (catsData?.data || []);

  /* ── Fetch post for edit ── */
  const { data: post, isLoading: postLoading } = useQuery({
    queryKey: ['admin-blog-post', id],
    queryFn: () => blogApi.get(id),
    enabled: isEdit,
  });

  useEffect(() => {
    if (post) {
      setForm({
        title:            post.title || '',
        slug:             post.slug || '',
        excerpt:          post.excerpt || '',
        content:          post.content || '',
        blog_category_id: post.category?.id || '',
        status:           post.status || 'draft',
        is_featured:      post.is_featured || false,
        published_at:     post.published_at ? post.published_at.replace(' ', 'T').slice(0, 16) : '',
        tags:             post.tags || [],
        meta_title:       post.meta_title || '',
        meta_description: post.meta_description || '',
        meta_keywords:    post.meta_keywords || '',
        canonical_url:    post.canonical_url || '',
        og_title:         post.og_title || '',
        og_description:   post.og_description || '',
        og_image:         post.og_image || '',
      });
      if (post.featured_image) setImagePreview(post.featured_image);
      setAutoSlug(false);
    }
  }, [post]);

  /* Auto-generate slug from title */
  useEffect(() => {
    if (autoSlug && form.title) {
      set('slug', slugify(form.title));
    }
  }, [form.title, autoSlug]);

  /* Auto-fill SEO meta_title from title */
  useEffect(() => {
    if (!form.meta_title && form.title) {
      set('meta_title', form.title);
    }
  }, [form.title]);

  /* ── Image handlers ── */
  function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleOgImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setOgImageFile(file);
    setOgImagePreview(URL.createObjectURL(file));
  }

  /* ── Tags ── */
  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) {
      set('tags', [...form.tags, t]);
    }
    setTagInput('');
  }

  function removeTag(t) {
    set('tags', form.tags.filter(x => x !== t));
  }

  /* ── Build FormData ── */
  function buildFormData(data) {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      if (k === 'tags') { fd.append(k, JSON.stringify(v)); return; }
      if (k === 'is_featured') { fd.append(k, v ? '1' : '0'); return; }
      fd.append(k, v);
    });
    if (imageFile) fd.append('featured_image', imageFile);
    if (ogImageFile) fd.append('og_image_file', ogImageFile);
    return fd;
  }

  /* ── Save mutation ── */
  const saveMut = useMutation({
    mutationFn: (d) => {
      const fd = buildFormData(d);
      return isEdit ? blogApi.update(id, fd) : blogApi.create(fd);
    },
    onSuccess: (res) => {
      qc.invalidateQueries(['admin-blog']);
      toast.success(isEdit ? 'Post updated!' : 'Post created!');
      navigate('/blog');
    },
    onError: (e) => {
      toast.error(e.message || 'Save failed');
      if (e.errors) {
        Object.values(e.errors).flat().forEach(msg => toast.error(msg));
      }
    },
  });

  function handleSave(overrideStatus) {
    const data = { ...form };
    if (overrideStatus) data.status = overrideStatus;
    if (!data.title.trim()) { toast.error('Title is required'); setActiveTab('content'); return; }
    if (!data.content.trim()) { toast.error('Content is required'); setActiveTab('content'); return; }
    saveMut.mutate(data);
  }

  if (isEdit && postLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* ── SEO preview ── */
  const previewTitle = form.meta_title || form.title || 'Page Title';
  const previewDesc  = form.meta_description || form.excerpt || 'Page description will appear here in search results.';
  const previewSlug  = form.slug || 'post-slug';

  return (
    <div className="max-w-5xl mx-auto space-y-5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/blog" className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#888] hover:text-white transition-all">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white">{isEdit ? 'Edit Post' : 'New Blog Post'}</h1>
            {isEdit && post && <p className="text-xs text-[#555] mt-0.5">Last updated {post.updated_at || ''}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={saveMut.isPending}
            className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-all disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            onClick={() => handleSave('published')}
            disabled={saveMut.isPending}
            className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
          >
            <Save size={14} />
            {saveMut.isPending ? 'Saving…' : isEdit ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className="grid lg:grid-cols-3 gap-5">

        {/* Left: Tabs + Content */}
        <div className="lg:col-span-2 space-y-4">

          {/* Tab bar */}
          <div className="flex gap-1 bg-[#151515] border border-white/5 rounded-xl p-1">
            {TABS.map(({ id: tid, label, icon: Icon }) => (
              <button
                key={tid}
                onClick={() => setActiveTab(tid)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                  activeTab === tid
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20'
                    : 'text-[#666] hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon size={12} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* ── Content Tab ── */}
          {activeTab === 'content' && (
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Title <span className="text-red-400">*</span></label>
                <input
                  value={form.title}
                  onChange={e => set('title', e.target.value)}
                  placeholder="Enter post title…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-3 text-white text-base font-medium placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
              </div>

              {/* Slug */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#555]">Slug (URL)</label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <div onClick={() => setAutoSlug(v => !v)} className={`w-7 h-4 rounded-full relative transition-all ${autoSlug ? 'bg-[#D4AF37]' : 'bg-white/10'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${autoSlug ? 'left-3.5' : 'left-0.5'}`} />
                    </div>
                    <span className="text-[11px] text-[#555]">Auto-generate</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#444] text-xs">/blog/</span>
                  <input
                    value={form.slug}
                    onChange={e => { setAutoSlug(false); set('slug', slugify(e.target.value)); }}
                    placeholder="post-url-slug"
                    className="flex-1 bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 font-mono"
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#555]">Excerpt</label>
                  <CharCount value={form.excerpt} max={500} />
                </div>
                <textarea
                  value={form.excerpt}
                  onChange={e => set('excerpt', e.target.value)}
                  rows={2}
                  placeholder="Short summary displayed in blog listing…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Content <span className="text-red-400">*</span></label>
                <textarea
                  value={form.content}
                  onChange={e => set('content', e.target.value)}
                  rows={18}
                  placeholder="Write your post content here (supports HTML)…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-y font-mono leading-relaxed"
                />
                <p className="text-[11px] text-[#444] mt-1">Supports HTML tags for rich formatting.</p>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Tags</label>
                <div className="flex gap-2">
                  <input
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
                    placeholder="Add tag and press Enter…"
                    className="flex-1 bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                  />
                  <button onClick={addTag} className="px-3 py-2 rounded-xl text-xs bg-white/5 hover:bg-white/10 text-[#888] hover:text-white transition-all">
                    <Tag size={12} />
                  </button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.tags.map(t => (
                      <span key={t} className="flex items-center gap-1 text-xs bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20 px-2 py-0.5 rounded-full">
                        {t}
                        <button onClick={() => removeTag(t)} className="hover:text-red-400 transition-colors"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SEO Tab ── */}
          {activeTab === 'seo' && (
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-5">

              {/* Search Preview */}
              <div className="bg-[#0F0F0F] border border-white/5 rounded-xl p-4">
                <p className="text-[11px] text-[#555] uppercase tracking-wider mb-3">Google Search Preview</p>
                <div className="space-y-0.5">
                  <p className="text-xs text-[#555]">https://decentwoods.com/blog/<span className="text-[#888]">{previewSlug}</span></p>
                  <p className="text-[#4A90D9] text-sm font-medium hover:underline cursor-pointer line-clamp-1">{previewTitle}</p>
                  <p className="text-[#888] text-xs line-clamp-2 leading-relaxed">{previewDesc}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#555]">Meta Title</label>
                  <CharCount value={form.meta_title} max={60} warn={0.8} />
                </div>
                <input
                  value={form.meta_title}
                  onChange={e => set('meta_title', e.target.value)}
                  placeholder="SEO title (defaults to post title)"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
                <p className="text-[11px] text-[#444] mt-1">Optimal length: 50–60 characters.</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#555]">Meta Description</label>
                  <CharCount value={form.meta_description} max={160} warn={0.85} />
                </div>
                <textarea
                  value={form.meta_description}
                  onChange={e => set('meta_description', e.target.value)}
                  rows={3}
                  placeholder="Compelling description shown in search results…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none"
                />
                <p className="text-[11px] text-[#444] mt-1">Optimal length: 120–160 characters.</p>
              </div>

              <div>
                <label className="block text-xs text-[#555] mb-1.5">Meta Keywords</label>
                <input
                  value={form.meta_keywords}
                  onChange={e => set('meta_keywords', e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
                <p className="text-[11px] text-[#444] mt-1">Comma-separated. Less important for Google, but useful for Bing.</p>
              </div>

              <div>
                <label className="block text-xs text-[#555] mb-1.5">Canonical URL</label>
                <input
                  value={form.canonical_url}
                  onChange={e => set('canonical_url', e.target.value)}
                  placeholder="https://decentwoods.com/blog/post-slug (leave blank for auto)"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 font-mono text-xs"
                />
                <p className="text-[11px] text-[#444] mt-1">Use to avoid duplicate content. Leave blank to auto-generate.</p>
              </div>
            </div>
          )}

          {/* ── Open Graph Tab ── */}
          {activeTab === 'og' && (
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-5">

              {/* OG Preview */}
              <div className="bg-[#0F0F0F] border border-white/5 rounded-xl overflow-hidden">
                <p className="text-[11px] text-[#555] uppercase tracking-wider p-4 pb-0">Social Share Preview</p>
                <div className="m-4 border border-white/5 rounded-xl overflow-hidden">
                  {(ogImagePreview || imagePreview) ? (
                    <img src={ogImagePreview || imagePreview} alt="OG preview" className="w-full h-36 object-cover" />
                  ) : (
                    <div className="w-full h-36 bg-[#1A1A1A] flex items-center justify-center">
                      <Share2 size={24} className="text-[#333]" />
                    </div>
                  )}
                  <div className="p-3 bg-[#151515] border-t border-white/5">
                    <p className="text-[10px] text-[#444] uppercase">decentwoods.com</p>
                    <p className="text-sm text-white font-medium mt-0.5 line-clamp-1">{form.og_title || form.meta_title || form.title || 'Post Title'}</p>
                    <p className="text-xs text-[#666] mt-0.5 line-clamp-2">{form.og_description || form.meta_description || form.excerpt || 'Post description…'}</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#555] mb-1.5">OG Title</label>
                <input
                  value={form.og_title}
                  onChange={e => set('og_title', e.target.value)}
                  placeholder="Title for social sharing (defaults to meta title)"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs text-[#555]">OG Description</label>
                  <CharCount value={form.og_description} max={200} />
                </div>
                <textarea
                  value={form.og_description}
                  onChange={e => set('og_description', e.target.value)}
                  rows={3}
                  placeholder="Description for social sharing…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs text-[#555] mb-2">OG Image (1200×630 recommended)</label>
                {ogImagePreview ? (
                  <div className="relative rounded-xl overflow-hidden border border-white/5">
                    <img src={ogImagePreview} alt="OG image" className="w-full h-40 object-cover" />
                    <button
                      onClick={() => { setOgImageFile(null); setOgImagePreview(null); }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-red-500/80"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => ogImageRef.current?.click()}
                    className="w-full h-28 border border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5 transition-all"
                  >
                    <Upload size={18} className="text-[#444]" />
                    <span className="text-xs text-[#555]">Upload OG image</span>
                    <span className="text-[11px] text-[#444]">Recommended: 1200×630px</span>
                  </button>
                )}
                <input ref={ogImageRef} type="file" accept="image/*" className="hidden" onChange={handleOgImageChange} />
                <p className="text-[11px] text-[#444] mt-1.5">Leave blank to use the featured image.</p>
              </div>
            </div>
          )}

          {/* ── Publish Tab ── */}
          {activeTab === 'publish' && (
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Publication Status</label>
                <div className="grid grid-cols-3 gap-2">
                  {['draft', 'published', 'scheduled'].map(s => (
                    <button
                      key={s}
                      onClick={() => set('status', s)}
                      className={`py-2.5 rounded-xl text-xs font-medium border capitalize transition-all ${
                        form.status === s
                          ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30'
                          : 'bg-[#121212] text-[#666] border-white/5 hover:border-white/10 hover:text-white'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {form.status === 'scheduled' && (
                <div>
                  <label className="block text-xs text-[#555] mb-1.5">Schedule Date & Time</label>
                  <input
                    type="datetime-local"
                    value={form.published_at}
                    onChange={e => set('published_at', e.target.value)}
                    className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"
                  />
                </div>
              )}

              {form.status === 'published' && (
                <div>
                  <label className="block text-xs text-[#555] mb-1.5">Published Date (optional override)</label>
                  <input
                    type="datetime-local"
                    value={form.published_at}
                    onChange={e => set('published_at', e.target.value)}
                    className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"
                  />
                </div>
              )}

              {/* Quick actions */}
              <div className="pt-2 flex gap-3">
                <button
                  onClick={() => handleSave('draft')}
                  disabled={saveMut.isPending}
                  className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-white/10 text-[#888] bg-white/5 hover:bg-white/10 disabled:opacity-50 transition-all"
                >
                  Save as Draft
                </button>
                <button
                  onClick={() => handleSave('published')}
                  disabled={saveMut.isPending}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
                >
                  <Save size={14} />{saveMut.isPending ? 'Saving…' : 'Publish Now'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-4">

          {/* Featured Image */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-[#555] mb-3 font-medium uppercase tracking-wider">Featured Image</p>
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-white/5">
                <img src={imagePreview} alt="Featured" className="w-full h-40 object-cover" />
                <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="flex gap-2">
                    <button onClick={() => imageRef.current?.click()} className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30">
                      <Upload size={13} />
                    </button>
                    <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="w-8 h-8 rounded-lg bg-red-500/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-red-500/60">
                      <X size={13} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => imageRef.current?.click()}
                className="w-full h-36 border border-dashed border-white/10 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-[#D4AF37]/30 hover:bg-[#D4AF37]/5 transition-all"
              >
                <Upload size={18} className="text-[#444]" />
                <span className="text-xs text-[#555]">Upload image</span>
                <span className="text-[11px] text-[#444]">JPG, PNG · Max 5MB</span>
              </button>
            )}
            <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
          </div>

          {/* Category */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-[#555] mb-3 font-medium uppercase tracking-wider">Category</p>
            <select
              value={form.blog_category_id}
              onChange={e => set('blog_category_id', e.target.value)}
              className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30"
            >
              <option value="">No category</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Options */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-[#555] mb-3 font-medium uppercase tracking-wider">Options</p>
            <div className="space-y-3">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <div onClick={() => set('is_featured', !form.is_featured)} className={`w-9 h-5 rounded-full relative transition-all ${form.is_featured ? 'bg-[#D4AF37]' : 'bg-white/10'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form.is_featured ? 'left-4' : 'left-0.5'}`} />
                </div>
                <span className="text-sm text-[#888]">Featured post</span>
                <Star size={12} className={form.is_featured ? 'text-[#D4AF37]' : 'text-[#555]'} />
              </label>
            </div>
          </div>

          {/* Status badge */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-[#555] mb-3 font-medium uppercase tracking-wider">Status</p>
            <div className="grid grid-cols-1 gap-1.5">
              {['draft', 'published', 'scheduled'].map(s => (
                <button
                  key={s}
                  onClick={() => set('status', s)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize ${
                    form.status === s
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20'
                      : 'text-[#666] hover:text-white hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${s === 'published' ? 'bg-green-400' : s === 'scheduled' ? 'bg-blue-400' : 'bg-yellow-400'}`} />
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* SEO quick check */}
          <div className="bg-[#151515] border border-white/5 rounded-xl p-4">
            <p className="text-xs text-[#555] mb-3 font-medium uppercase tracking-wider">SEO Checklist</p>
            <div className="space-y-2">
              {[
                { ok: !!form.title,            label: 'Post title set' },
                { ok: !!form.slug,             label: 'URL slug set' },
                { ok: !!form.excerpt,          label: 'Excerpt added' },
                { ok: !!form.meta_title,       label: 'Meta title set' },
                { ok: !!form.meta_description, label: 'Meta description set' },
                { ok: form.meta_title.length <= 60,        label: 'Meta title ≤ 60 chars' },
                { ok: form.meta_description.length <= 160, label: 'Meta desc ≤ 160 chars' },
                { ok: !!imagePreview,          label: 'Featured image set' },
                { ok: !!form.blog_category_id, label: 'Category selected' },
              ].map(({ ok, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? 'bg-green-500/20' : 'bg-white/5'}`}>
                    {ok
                      ? <span className="text-green-400 text-[9px] font-bold">✓</span>
                      : <span className="text-[#444] text-[9px]">–</span>
                    }
                  </div>
                  <span className={`text-[11px] ${ok ? 'text-[#888]' : 'text-[#555]'}`}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
